/**
 * Клиент локального API MetaScalp (§89, `knowledge/terminals.md`): HTTP на
 * 127.0.0.1, порт из 17845–17855. Без React: `fetch` приходит параметром, и
 * тест подставляет свой. Соединения нет — клик это один `POST /api/change-ticker`
 * без повторов (§89 п. 7).
 */

export const METASCALP_FIRST_PORT = 17845;
export const METASCALP_LAST_PORT = 17855;

const HOST = "127.0.0.1";
const DEFAULT_PING_TIMEOUT_MS = 800;
const DEFAULT_CHANGE_TIMEOUT_MS = 3000;

/** Подмножество `fetch`, которое нужно клиенту; `globalThis.fetch` ему удовлетворяет. */
export type MetascalpFetch = (url: string, init?: RequestInit) => Promise<Response>;

/** Тело `change-ticker`; `binding` — номер связки строкой из трёх цифр. */
export interface ChangeTickerBody {
  exchange: number;
  market: number;
  ticker: string;
  binding?: string;
}

export type MetascalpOutcome =
  /** `200`: MetaScalp принял команду. */
  | { kind: "ok"; port: number }
  /** `400`/иной отказ MetaScalp с текстом ошибки. */
  | { kind: "rejected"; message: string }
  /** Запрос не дошёл: порт не найден, соединение отказано или нет ответа в срок. */
  | { kind: "not_sent" };

/** Что известно о порте — для подсказки действия. */
export type MetascalpState =
  | { kind: "idle" }
  | { kind: "found"; port: number }
  | { kind: "not_found" }
  /** Порт задан вручную и не ответил: «порт не найден» тут не годится — искать нечего. */
  | { kind: "manual_down"; port: number };

export interface DiscoverOptions {
  pingTimeoutMs?: number;
}

export interface MetascalpLinkOptions extends DiscoverOptions {
  changeTimeoutMs?: number;
}

async function pingsAsMetascalp(
  fetchFn: MetascalpFetch,
  port: number,
  timeoutMs: number,
): Promise<boolean> {
  try {
    const response = await fetchFn(`http://${HOST}:${port}/ping`, {
      signal: AbortSignal.timeout(timeoutMs),
    });
    const body: unknown = await response.json();
    return typeof body === "object" && body !== null && "app" in body && body.app === "MetaScalp";
  } catch {
    // Отказ соединения, таймаут, не JSON — порт не MetaScalp.
    return false;
  }
}

/**
 * Опрашивает `GET /ping` на всех портах сразу и берёт наименьший, чей ответ —
 * JSON с `"app": "MetaScalp"` (§89 п. 3). `null` — ни один не ответил.
 */
export async function discoverPort(
  fetchFn: MetascalpFetch,
  options: DiscoverOptions = {},
): Promise<number | null> {
  const timeoutMs = options.pingTimeoutMs ?? DEFAULT_PING_TIMEOUT_MS;
  const ports = Array.from(
    { length: METASCALP_LAST_PORT - METASCALP_FIRST_PORT + 1 },
    (_, index) => METASCALP_FIRST_PORT + index,
  );
  const answers = await Promise.all(
    ports.map((port) => pingsAsMetascalp(fetchFn, port, timeoutMs)),
  );
  const index = answers.indexOf(true);
  return index === -1 ? null : (ports[index] ?? null);
}

async function errorMessageOf(response: Response): Promise<string> {
  try {
    const body: unknown = await response.json();
    if (typeof body === "object" && body !== null && "error" in body) {
      const { error } = body;
      if (typeof error === "string" && error !== "") {
        return error;
      }
    }
  } catch {
    // Тело не JSON — остаётся статус.
  }
  return `HTTP ${response.status}`;
}

export interface MetascalpLink {
  /**
   * Одна попытка отправки. `manualPort` — порт из настроек: с ним опроса нет;
   * `null` — запомненный или найденный опросом.
   */
  send: (manualPort: number | null, body: ChangeTickerBody) => Promise<MetascalpOutcome>;
  /** Поиск порта без отправки (кнопка «Найти» в настройках). */
  find: () => Promise<number | null>;
  getState: () => MetascalpState;
  subscribe: (listener: () => void) => () => void;
}

export function createMetascalpLink(
  fetchFn: MetascalpFetch,
  options: MetascalpLinkOptions = {},
): MetascalpLink {
  const changeTimeoutMs = options.changeTimeoutMs ?? DEFAULT_CHANGE_TIMEOUT_MS;
  const listeners = new Set<() => void>();
  // Найденный порт живёт до первой ошибки соединения с ним (§89 п. 3).
  let remembered: number | null = null;
  let state: MetascalpState = { kind: "idle" };

  const setState = (next: MetascalpState): void => {
    if (JSON.stringify(next) === JSON.stringify(state)) {
      return;
    }
    state = next;
    for (const listener of listeners) {
      listener();
    }
  };

  // Идущий поиск один на всех: клики и «Найти» во время него получают тот же промис.
  let discovering: Promise<number | null> | null = null;

  const find = (): Promise<number | null> => {
    if (discovering !== null) {
      return discovering;
    }
    const run = discoverPort(fetchFn, options)
      .then((port) => {
        remembered = port;
        setState(port === null ? { kind: "not_found" } : { kind: "found", port });
        return port;
      })
      .finally(() => {
        discovering = null;
      });
    discovering = run;
    return run;
  };

  return {
    async send(manualPort, body) {
      const port = manualPort ?? remembered ?? (await find());
      if (port === null) {
        return { kind: "not_sent" };
      }
      let response: Response;
      try {
        response = await fetchFn(`http://${HOST}:${port}/api/change-ticker`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
          signal: AbortSignal.timeout(changeTimeoutMs),
        });
      } catch {
        if (manualPort === null) {
          remembered = null;
          setState({ kind: "not_found" });
        } else {
          setState({ kind: "manual_down", port: manualPort });
        }
        return { kind: "not_sent" };
      }
      // Запоминается только найденный опросом порт: ручной берётся из настроек каждый раз.
      if (manualPort === null) {
        remembered = port;
      }
      setState({ kind: "found", port });
      if (response.ok) {
        return { kind: "ok", port };
      }
      return { kind: "rejected", message: await errorMessageOf(response) };
    },
    find,
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/** Один клиент на вкладку: найденный порт общий для всех кнопок. */
export const metascalpLink = createMetascalpLink((url, init) => fetch(url, init));
